/**
 * @documental
 * @export Tab
 * @name
 * TabSet.Tab
 */

import { FC, ReactNode } from "react";
import { BaseProps } from "../../BaseProps";
import { AsyncTabChangeHandler } from "./AsyncTabChangeHandler";
import { TabChangeHandler } from "./TabChangeHandler";
import { TooltipProps } from "../Tooltip";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The tab component properties.
 */
export interface TabProps extends BaseProps, AccessibilityProps {
  /**
   * The tab ID.
   */
  readonly id: string;

  /**
   * Indicates whether the tab is disabled. Disabling a tab does not prevent it being selected through the `TabSet.selectedTab`
   * property.
   */
  readonly disabled?: boolean;

  /**
   * The tab tooltip. The tooltip appearance is able to be adjusted on the parent `TabSet` component.
   */
  readonly tooltip?: TooltipProps["content"];

  /**
   * Called before the tab is selected. Allows for an asynchronous operation to occur prior to the tab being selected.
   * While the Promise is unresolved, tab navigation will wait. If an exception is thrown during navigation, the navigation
   * attempt will be halted and the offending tab will be highlighted.
   * <br>
   * <br>
   * **NOTE:** Unlike onBeforeUnselect, while a pending asynchronous operation is ocurring users are able to navigate to another tab
   * and to cancel the current pending select.
   */
  readonly onBeforeSelect?: AsyncTabChangeHandler;

  /**
   * Called when the current tab is selected. The recommended method to respond to selected tab changes when
   * using asynchronous tabs.
   */
  readonly onSelect?: TabChangeHandler;

  /**
   * Called before the tab is unselected. Allows for an asynchronous operation to occur prior to the tab being unselected,
   * such as cleanup or saving of a form. While the Promise is unresolved, tab navigation will wait. If an exception
   * is thrown during navigation, the navigation attempt will be halted and the offending tab will be highlighted.
   * <br>
   * <br>
   * **NOTE:** While a pending asynchronous operation is occurring, further tab navigation is prevented.
   */
  readonly onBeforeUnselect?: AsyncTabChangeHandler;

  /**
   * Called when the current tab is unselected.
   */
  readonly onUnselect?: TabChangeHandler;

  /**
   * The tab children.
   */
  readonly children?: ReactNode;
}

/**
 * Represents a single tab which can be placed within the TabSet component.
 */
export const Tab: FC<TabProps> = () => {
  // Tab actually renders nothing, is just a placeholder for its data.
  return null;
};
